/**
 * 🧪 A/B 테스트 유틸리티
 * 
 * 랜딩 페이지 메시지 등에 대한 A/B 테스트를 수행합니다.
 * localStorage 기반으로 사용자에게 일관된 변형을 보여줍니다.
 * 
 * 사용 예시:
 * const variant = getABTestVariant('landing_hero_message');
 * if (variant === 'A') { ... } else { ... }
 */

const AB_TEST_STORAGE_KEY = 'whatquiz_ab_tests';

/**
 * A/B 테스트 설정
 * 각 테스트의 변형과 가중치를 정의합니다.
 */
export const AB_TEST_CONFIG = {
  // 🎯 랜딩 페이지 히어로 메시지 테스트 (비활성화 → 타이틀/서브타이틀 고정)
  landing_hero_message: {
    enabled: false,
    variants: {
      A: {
        title: '생기부,',
        titleHighlight: '3분이면 됩니다',
        subtitle: '7시간 걸리던 작업을 AI가 3분으로 줄여드립니다.',
        ctaText: '무료로 생기부 써보기'
      }
    },
    weights: { A: 100 }
  },
  
  // 🎯 CTA 버튼 텍스트 (A/B 테스트 종료 → "무료로 생기부 써보기" 확정)
  landing_cta_button: {
    enabled: false,
    variants: {
      A: { text: '무료로 생기부 써보기' }
    },
    weights: { A: 100 }
  }
};

/**
 * 저장된 A/B 테스트 할당 조회
 */
const getStoredAssignments = () => {
  try {
    const stored = localStorage.getItem(AB_TEST_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * A/B 테스트 할당 저장
 */
const saveAssignment = (testName, variant) => {
  try {
    const assignments = getStoredAssignments();
    assignments[testName] = {
      variant,
      assignedAt: new Date().toISOString()
    };
    localStorage.setItem(AB_TEST_STORAGE_KEY, JSON.stringify(assignments));
  } catch {
    // localStorage 실패 시 무시
  }
};

/**
 * 가중치 기반 랜덤 변형 선택
 */
const selectWeightedVariant = (weights) => {
  const variants = Object.keys(weights);
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  
  let random = Math.random() * totalWeight;
  
  for (const variant of variants) {
    random -= weights[variant];
    if (random <= 0) {
      return variant;
    }
  }
  
  return variants[0]; // 폴백
};

/**
 * A/B 테스트 변형 가져오기
 * 
 * @param {string} testName - 테스트 이름 (AB_TEST_CONFIG의 키)
 * @returns {string} 할당된 변형 ('A', 'B', 'C' 등)
 */
export const getABTestVariant = (testName) => {
  const config = AB_TEST_CONFIG[testName];
  
  // 테스트가 없거나 비활성화된 경우 기본 변형 반환
  if (!config || !config.enabled) {
    return 'A';
  }
  
  // 이미 할당된 변형이 있는지 확인
  const assignments = getStoredAssignments();
  if (assignments[testName]?.variant) {
    // 해당 변형이 아직 유효한지 확인
    if (config.variants[assignments[testName].variant]) {
      return assignments[testName].variant;
    }
  }
  
  // 새 변형 할당
  const variant = selectWeightedVariant(config.weights);
  saveAssignment(testName, variant);
  
  return variant;
};

/**
 * 현재 테스트의 변형 데이터 가져오기
 * 
 * @param {string} testName - 테스트 이름
 * @returns {object} 변형 데이터
 */
export const getABTestData = (testName) => {
  const config = AB_TEST_CONFIG[testName];
  
  if (!config || !config.enabled) {
    return config?.variants?.A || {};
  }
  
  const variant = getABTestVariant(testName);
  return config.variants[variant] || config.variants.A;
};

/**
 * A/B 테스트 이벤트 추적
 * GA4 및 자체 분석 시스템에 이벤트 전송
 * 
 * @param {string} testName - 테스트 이름
 * @param {string} action - 이벤트 액션 (view, click, convert 등)
 * @param {object} additionalData - 추가 데이터
 */
export const trackABTestEvent = (testName, action, additionalData = {}) => {
  const variant = getABTestVariant(testName);
  
  // GA4 이벤트 전송
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', `ab_test_${action}`, {
      test_name: testName,
      variant,
      ...additionalData
    });
  }
  
  // 콘솔 로그 제거됨 (GA4로 충분)
};

/**
 * 모든 A/B 테스트 할당 초기화 (디버깅용)
 */
export const resetAllABTests = () => {
  try {
    localStorage.removeItem(AB_TEST_STORAGE_KEY);
  } catch {
    // 무시
  }
};

/**
 * 특정 테스트 강제 할당 (디버깅용)
 */
export const forceABTestVariant = (testName, variant) => {
  saveAssignment(testName, variant);
};

// 기본 export
const abTest = {
  AB_TEST_CONFIG,
  getABTestVariant,
  getABTestData,
  trackABTestEvent,
  resetAllABTests,
  forceABTestVariant
};

export default abTest;
